import React from 'react';
import type { LabeledKpiCardViz } from 'shared/src/api/ai-labeled-viz-types';
import type { ValueFormat } from 'shared/src/api/ai-viz-types';
import { transformKpiCard, formatVizValue } from 'shared/src/utils/ai/ai-viz-transformers';
import type { CubeRow } from './chart-utils';

type KpiCardProps = {
  rows: CubeRow[];
  viz: LabeledKpiCardViz;
};

export function KpiCard({ rows, viz }: KpiCardProps) {
  const data = transformKpiCard(rows, viz);
  const valueFormat = viz.labels.valueFormat;

  if (rows.length === 0 || data.value === null) {
    return (
      <div className="w-full bg-white rounded-2xl shadow-lg p-6">
        <h2 className="text-2xl font-semibold mb-4">{viz.title}</h2>
        <div className="flex items-center justify-center" style={{ height: 120 }}>
          <p className="text-gray-400 text-lg">No data available</p>
        </div>
      </div>
    );
  }

  const displayValue = formatVizValue(data.value, valueFormat);
  const showDelta = viz.mapping.deltaKey && data.delta !== null;

  return (
    <div className="w-full bg-white rounded-2xl shadow-lg p-6">
      <h2 className="text-2xl font-semibold mb-1">{viz.title}</h2>
      <p className="text-xs text-gray-500 mb-4">{viz.labels.valueLabel}</p>

      <p className="text-4xl font-bold tabular-nums mb-3">{displayValue}</p>

      {showDelta ? (
        <DeltaPill
          delta={data.delta!}
          deltaFormat={viz.labels.deltaFormat ?? 'percent'}
          label={viz.deltaLabel}
        />
      ) : null}
    </div>
  );
}

// ─── Internal helpers (in call order) ───

function DeltaPill({
  delta,
  deltaFormat,
  label
}: {
  delta: number;
  deltaFormat: ValueFormat;
  label?: string;
}) {
  const isPositive = delta > 0;
  const isNegative = delta < 0;

  const bgColor = isPositive ? 'bg-green-100' : isNegative ? 'bg-red-100' : 'bg-gray-100';
  const textColor = isPositive ? 'text-green-700' : isNegative ? 'text-red-700' : 'text-gray-700';
  const arrow = isPositive ? '\u25B2' : isNegative ? '\u25BC' : '';
  const sign = isPositive ? '+' : '';
  const formatted = `${sign}${formatVizValue(delta, deltaFormat)}`;

  return (
    <div className="flex items-center gap-2">
      <span
        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-sm font-medium ${bgColor} ${textColor}`}>
        {arrow ? <span className="text-xs">{arrow}</span> : null}
        {formatted}
      </span>
      {label ? <span className="text-xs text-gray-500">{label}</span> : null}
    </div>
  );
}
